/**
 * @fileoverview Servicio de sincronización - Integra con microservicio sync.nubelytics.com
 * Implementa múltiples patrones de diseño: Repository, Command, Strategy
 * siguiendo principios SOLID completos para garantizar mantenibilidad y escalabilidad.
 * 
 * @module syncService
 * @author Nubelytics Engineering
 * @version 1.1.0
 */

import { db } from '../config/firebase';
import userDataService from './userDataService';
import appConfig from '../infrastructure/config/AppConfig';
import { buildApiUrl, buildSyncUrl } from '../utils/apiUrlBuilder';

// Constantes de configuración
const ENDPOINTS = {
  // URLs del microservicio de sincronización
  SYNC_DOMAIN: 'sync.nubelytics.com',
  SYNC_DOMAIN_FALLBACK: 'sync-nubelytics.web.app',
  
  // Endpoints del servicio de sincronización
  PROCESS: 'process',
  HEALTHCHECK: 'healthcheck',
  SALES: 'sales',
  PRODUCTS: 'products',
  ORDERS: 'orders',
  CUSTOMERS: 'customers',
  
  // URLs de Firebase Functions para compatibilidad
  PROCESS_SYNC_V1: 'https://us-central1-nubelytics.cloudfunctions.net/processSyncV1',
  HEALTHCHECK_V1: 'https://us-central1-nubelytics.cloudfunctions.net/healthcheckV1',
  
  // URLs fallback con formato relativo para entornos de desarrollo
  PROCESS_SYNC_FALLBACK: '/api/sync/process',
  HEALTHCHECK_FALLBACK: '/api/sync/healthcheck'
};

// Cache para resultados de sincronización reciente - Patrón Cache
const syncResultsCache = {
  results: {},
  setResult(entityType, result) {
    this.results[entityType] = {
      data: result,
      timestamp: Date.now()
    };
  },
  getResult(entityType) {
    const cached = this.results[entityType];
    if (cached && Date.now() - cached.timestamp < 300000) { // 5 minutos
      return cached.data;
    }
    return null;
  },
  clearCache() {
    this.results = {};
  }
};

/**
 * Fábrica de URL para endpoints de sincronización - Patrón Factory
 * Determina la URL óptima basada en ambiente y disponibilidad de servicios
 * @param {string} endpoint - Tipo de endpoint (process, healthcheck, sales, etc)
 * @returns {string} URL completa para el endpoint
 */
const getSyncEndpointUrl = (endpoint = 'process') => {
  try {
    // Registrar información de ambiente para diagnóstico
    if (appConfig.debug) {
      try {
        localStorage.setItem('nubelytics_sync_environment', appConfig.environment || 'unknown');
        localStorage.setItem('nubelytics_sync_host', window.location.host);
        localStorage.setItem('nubelytics_sync_endpoint', endpoint);
      } catch (e) {/* Error no crítico */}
    }

    // Determinar si endpoint es válido y si no, usar process genérico
    const validEndpoint = Object.values(ENDPOINTS).includes(endpoint) ? endpoint : ENDPOINTS.PROCESS;
    
    // 1. Primera prioridad: Usar appConfig.getSyncUrl si está disponible (nuevo método)
    if (typeof appConfig.getSyncUrl === 'function') {
      try {
        const syncUrl = appConfig.getSyncUrl(validEndpoint);
        console.log(`[syncService] URL desde appConfig.getSyncUrl: ${syncUrl}`);
        
        // Guardar información diagnóstica
        if (appConfig.debug) {
          try {
            localStorage.setItem('nubelytics_sync_url_from_config', syncUrl);
            localStorage.setItem('nubelytics_sync_valid_endpoint', validEndpoint);
          } catch (e) {/* Error no crítico */}
        }
        
        return syncUrl;
      } catch (configUrlError) {
        console.warn('[syncService] Error en appConfig.getSyncUrl:', configUrlError);
        // Continuar con estrategias alternativas
      }
    }
    
    // 2. Segunda prioridad: Usar buildSyncUrl para aprovechar el microservicio
    try {
      const syncUrl = buildSyncUrl(validEndpoint, {
        useApiAsFallback: true // Usar api.nubelytics.com si sync.nubelytics.com no está disponible
      });
      
      console.log(`[syncService] URL desde buildSyncUrl: ${syncUrl}`);
      return syncUrl;
    } catch (buildUrlError) {
      console.warn('[syncService] Error en buildSyncUrl:', buildUrlError);
      // Continuar con estrategias alternativas
    }
    
    // 3. Tercera prioridad: URLs específicas según ambiente
    const isProd = appConfig.environment === 'production' || 
                 window.location.host.includes('nubelytics.com');
    
    if (isProd) {
      // En producción, intentar construir URL directa al microservicio sync
      const protocol = 'https:';
      const domain = ENDPOINTS.SYNC_DOMAIN;
      return `${protocol}//${domain}/${validEndpoint}`;
    } else {
      // En desarrollo, usar URL relativas
      return endpoint === 'healthcheck' ? 
        buildApiUrl(ENDPOINTS.HEALTHCHECK_FALLBACK.replace('/api/', '')) : 
        buildApiUrl(ENDPOINTS.PROCESS_SYNC_FALLBACK.replace('/api/', ''));
    }
  } catch (error) {
    console.error('[syncService] Error construyendo URL:', error);
    
    // 4. Fallback a URL absoluta de Firebase Functions en caso de error
    // Este triple nivel de recuperación garantiza alta disponibilidad
    return endpoint === 'healthcheck' ? 
      ENDPOINTS.HEALTHCHECK_V1 : 
      ENDPOINTS.PROCESS_SYNC_V1;
  }
};

/**
 * Comprueba la salud del servicio de sincronización - Patrón Command
 * @returns {Promise<Object>} Estado del servicio
 */
export const checkSyncHealth = async () => {
  try {
    const url = getSyncEndpointUrl(ENDPOINTS.HEALTHCHECK);
    
    console.log('[syncService] Verificando estado del servicio:', url);
    
    // Registrar telemetría para diagnóstico avanzado
    try {
      localStorage.setItem('nubelytics_sync_health_request_url', url);
      localStorage.setItem('nubelytics_sync_health_request_time', new Date().toISOString());
    } catch (e) {/* Error no crítico */}
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache',
        'X-Request-Source': 'nubelytics-web'
      },
      // No incluimos credentials para evitar problemas CORS con Functions
    });
    
    if (!response.ok) {
      throw new Error(`Estado HTTP: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Registrar diagnóstico
    try {
      localStorage.setItem('nubelytics_sync_health_status', data.status || 'unknown');
      localStorage.setItem('nubelytics_sync_health_timestamp', data.timestamp || new Date().toISOString());
    } catch (e) {/* Error no crítico */}
    
    return {
      success: data.status === 'healthy',
      ...data
    };
  } catch (error) {
    console.error('[syncService] Error verificando salud del servicio:', error);
    return {
      success: false,
      status: 'error',
      error: error.message
    };
  }
};

/**
 * Procesa un trabajo de sincronización - Implementa patrones Command y Strategy
 * @param {Object} params Parámetros del trabajo
 * @param {string} params.tiendaId ID de la tienda
 * @param {string} params.entityType Tipo de entidad (productos, ventas, etc)
 * @param {Object} [params.options] Opciones adicionales para la sincronización
 * @returns {Promise<Object>} Resultado del trabajo de sincronización
 */
export const processSync = async ({ tiendaId, entityType, options = {} }) => {
  // Validación básica - Diseño por contrato
  if (!tiendaId || !entityType) {
    console.error('[syncService] Error: tiendaId y entityType son requeridos');
    return { 
      success: false,
      error: 'Parámetros incompletos', 
      timestamp: new Date().toISOString() 
    };
  }
  
  // Registrar solicitud para telemetría
  try {
    localStorage.setItem('nubelytics_sync_last_request', JSON.stringify({
      tiendaId,
      entityType,
      timestamp: new Date().toISOString(),
      options: JSON.stringify(options).substring(0, 200) // Limitar tamaño
    }));
  } catch (e) {/* Error no crítico */}
  
  // Verificar primero en caché - Patrón Cache
  if (!options.forceRefresh) {
    const cachedResult = syncResultsCache.getResult(`${tiendaId}:${entityType}`);
    if (cachedResult) {
      console.log(`[syncService] Usando resultado en caché para ${entityType}`);
      return cachedResult;
    }
  }
  
  try {
    // URL para procesar trabajo de sincronización
    // Obtener URL del endpoint usando el tipo de entidad si es un endpoint específico
    // o process como genérico - Aplicando Single Responsibility
    const specificEndpoint = ENDPOINTS[entityType.toUpperCase()];
    const url = getSyncEndpointUrl(specificEndpoint || ENDPOINTS.PROCESS);
    console.log(`[syncService] Iniciando sincronización de ${entityType} para tienda ${tiendaId}`);
    
    // Preparar datos del trabajo de sincronización
    const payload = {
      tiendaId,
      entityType,
      ...options,
      timestamp: new Date().toISOString(),
      source: 'frontend-sync'
    };
    
    // Usar fetch nativo para evitar dependencia de axios
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      throw new Error(`Estado HTTP: ${response.status}`);
    }
    
    const result = await response.json();
    
    // Almacenar en cache para futuros usos - Patrón Cache
    if (result.success && options.disableCache !== true) {
      syncResultsCache.setResult(`${tiendaId}:${entityType}`, result);
    }
    
    // Registrar diagnóstico detallado siguiendo patrón Observer
    try {
      localStorage.setItem('nubelytics_last_sync_entity', entityType);
      localStorage.setItem('nubelytics_last_sync_timestamp', new Date().toISOString());
      localStorage.setItem('nubelytics_last_sync_result', result.success ? 'success' : 'error');
    } catch (e) {/* Error no crítico */}
    
    return result;
  } catch (error) {
    console.error(`[syncService] Error en sincronización de ${entityType}:`, error);
    
    // Registrar diagnóstico de error
    try {
      localStorage.setItem('nubelytics_sync_error_entity', entityType);
      localStorage.setItem('nubelytics_sync_error_message', error.message);
      localStorage.setItem('nubelytics_sync_error_timestamp', new Date().toISOString());
    } catch (e) {/* Error no crítico */}
    
    // Intentar recuperación utilizando API de backup - Patrón Strategy
    return await processSyncFallback({ tiendaId, entityType, options })
      .catch(() => ({
        success: false,
        error: error.message,
        jobId: null,
        timestamp: new Date().toISOString()
      }));
  }
};

/**
 * Estrategia alternativa para sincronización - Patrón Strategy
 * @private
 */
const processSyncFallback = async ({ tiendaId, entityType, options = {} }) => {
  try {
    console.log(`[syncService] Usando estrategia alternativa para ${entityType}`);
    
    // 1. Primera opción: Intentar con el dominio sync-nubelytics.web.app
    try {
      const protocol = window.location.protocol || 'https:';
      const syncEndpoint = `${protocol}//${ENDPOINTS.SYNC_DOMAIN_FALLBACK}/${ENDPOINTS.PROCESS}`;
      
      const response = await fetch(syncEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Recovery-Attempt': 'sync-domain-fallback'
        },
        body: JSON.stringify({
          tiendaId,
          entityType,
          ...options,
          isRecoveryAttempt: true
        })
      });
      
      if (response.ok) {
        return await response.json();
      }
    } catch (syncDomainError) {
      console.warn('[syncService] Error en fallback de sync-domain:', syncDomainError);
    }
    
    // 2. Segunda opción: Usar fetch directo con el proxy CORS
    const apiBase = appConfig.getApiUrl('');
    const apiSyncEndpoint = `${apiBase}/sync/process`;
    
    // Obtener token de autenticación, si está disponible
    let authHeader = '';
    try {
      // Intentar obtener token del localStorage o sessionStorage
      const token = localStorage.getItem('nubelytics_auth_token') || 
                    sessionStorage.getItem('nubelytics_auth_token') || 
                    '';
      
      if (token) {
        authHeader = `Bearer ${token}`;
      }
    } catch (e) {
      console.warn('[syncService] No se pudo obtener token de autenticación:', e);
    }
    
    // Usar fetch con cabeceras de autenticación
    const response = await fetch(apiSyncEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Recovery-Attempt': 'api-sync-fallback',
        'Authorization': authHeader
      },
      body: JSON.stringify({
        tiendaId,
        entityType,
        ...options,
        isRecoveryAttempt: true
      })
    });
    
    // Verificar si la respuesta es exitosa
    if (!response.ok) {
      throw new Error(`Error en la sincronización: ${response.status} ${response.statusText}`);
    }
    
    // Procesar respuesta JSON
    const data = await response.json();
    console.log(`[syncService] Respuesta de recuperación para ${entityType}:`, data || 'Sin datos');
    
    return data || { 
      success: true, 
      recovery: true,
      message: 'Procesado via estrategia alternativa'
    };
  } catch (fallbackError) {
    console.error(`[syncService] Error en estrategia alternativa:`, fallbackError);
    throw fallbackError; // Propagar para manejo superior
  }
};

/**
 * Sincroniza entidad específica con opciones optimizadas
 * Implementa Factory Method para crear comando de sincronización apropiado
 * @param {string} tiendaId ID de la tienda
 * @param {string} entityType Tipo de entidad
 * @param {Object} options Opciones
 * @returns {Promise<Object>} Resultado 
 */
export const syncEntity = async (tiendaId, entityType, options = {}) => {
  // Estrategias específicas por entidad - Patrón Strategy
  const entityStrategies = {
    products: { useCache: true, priority: 'normal' },
    sales: { useCache: false, priority: 'high', forceRefresh: true },
    orders: { useCache: true, priority: 'high' },
    customers: { useCache: true, priority: 'low' }
  };
  
  // Combinar opciones predefinidas con las proporcionadas
  const mergedOptions = {
    ...entityStrategies[entityType] || { useCache: true },
    ...options
  };
  
  // Procesar sincronización con opciones combinadas
  return processSync({ 
    tiendaId, 
    entityType,
    options: mergedOptions
  });
};

/**
 * Sincroniza ventas recientes - Método específico para ventas
 * @param {string} tiendaId ID de la tienda
 * @param {Object} options Opciones adicionales
 */
export const syncRecentSales = async (tiendaId, options = {}) => {
  try {
    // Intentar utilizar el endpoint específico de ventas en el microservicio
    // si está disponible (principio de Interface Segregation)
    const url = getSyncEndpointUrl(ENDPOINTS.SALES);
    
    console.log(`[syncService] Sincronizando ventas recientes para tienda ${tiendaId}`);
    
    // Preparar payload específico para ventas
    const payload = {
      tiendaId,
      timeRange: options.timeRange || 'last_hour',
      forceRefresh: options.forceRefresh !== false,
      entityType: 'sales', // Para compatibilidad con versiones anteriores
      ...options
    };
    
    // Realizar petición
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Request-Source': 'nubelytics-web'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      throw new Error(`Error en sincronización de ventas: ${response.status}`);
    }
    
    const result = await response.json();
    
    // Almacenar en cache
    if (result.success && options.disableCache !== true) {
      syncResultsCache.setResult(`${tiendaId}:sales`, result);
    }
    
    return result;
  } catch (error) {
    console.warn('[syncService] Error usando endpoint específico de ventas:', error.message);
    
    // Fallback a método genérico
    return syncEntity(tiendaId, 'sales', {
      forceRefresh: true, 
      timeRange: 'last_hour',
      ...options
    });
  }
};

/**
 * Sincroniza todos los datos del dashboard (productos, órdenes y clientes)
 * @returns {Promise<Object>} Resultado de la sincronización con conteo de elementos
 */
const syncDashboard = async () => {
  try {
    const startTime = new Date();
    const results = {};
    const counts = {};
    
    // Registrar diagnósticos
    try {
      localStorage.setItem('dashboard_sync_started', startTime.toISOString());
    } catch (e) {/* Ignorar errores de localStorage */}
    
    // Ejecutar sincronización de productos
    try {
      const productsResult = await syncProducts();
      results.products = productsResult;
      counts.products = productsResult?.count || 0;
    } catch (error) {
      console.error('[syncService] Error en sincronización de productos:', error);
      results.productsError = error.message;
    }
    
    // Ejecutar sincronización de órdenes
    try {
      const ordersResult = await syncOrders();
      results.orders = ordersResult;
      counts.orders = ordersResult?.count || 0;
    } catch (error) {
      console.error('[syncService] Error en sincronización de órdenes:', error);
      results.ordersError = error.message;
    }
    
    // Ejecutar sincronización de clientes
    try {
      const customersResult = await syncCustomers();
      results.customers = customersResult;
      counts.customers = customersResult?.count || 0;
    } catch (error) {
      console.error('[syncService] Error en sincronización de clientes:', error);
      results.customersError = error.message;
    }
    
    const endTime = new Date();
    const duration = (endTime - startTime) / 1000; // duración en segundos
    
    // Registrar resultados finales
    try {
      localStorage.setItem('dashboard_sync_completed', endTime.toISOString());
      localStorage.setItem('dashboard_sync_duration', String(duration));
      localStorage.setItem('dashboard_sync_counts', JSON.stringify(counts));
    } catch (e) {/* Ignorar errores de localStorage */}
    
    return {
      success: true,
      count: counts,
      duration,
      results
    };
  } catch (error) {
    console.error('[syncService] Error en syncDashboard:', error);
    throw error;
  }
};

/**
 * Sincroniza productos con la tienda online
 * @param {string} tiendaId - ID de la tienda (opcional, usa userData por defecto)
 * @param {Object} options - Opciones adicionales
 * @returns {Promise<Object>} Resultado de la sincronización
 */
const syncProducts = async (tiendaId = null, options = {}) => {
  try {
    // Obtener el ID de tienda desde el servicio centralizado mejorado
    // UserDataService ahora garantiza que siempre devolverá un ID válido o un ID de error
    const storeId = tiendaId || userDataService.getStoreId();
    
    // Verificar que no sea el ID de error
    if (storeId === 'ERROR_NO_STORE_ID') {
      console.error('[syncProducts] No se pudo obtener un storeId válido para la sincronización');
      throw new Error('ID de tienda no disponible o no válido');
    }
    
    // Registrar diagnósticos
    try {
      localStorage.setItem('products_sync_start', new Date().toISOString());
    } catch (e) {/* Ignorar errores de localStorage */}
    
    // Utilizar la función syncEntity con el tipo 'products'
    const result = await syncEntity(storeId, 'products', {
      forceRefresh: options.forceRefresh !== false,
      ...options
    });
    
    // Registrar resultado
    try {
      localStorage.setItem('products_sync_result', JSON.stringify({
        success: result?.success,
        count: result?.count,
        timestamp: new Date().toISOString()
      }));
    } catch (e) {/* Ignorar errores de localStorage */}
    
    return result;
  } catch (error) {
    // Registrar error
    console.error('[syncService] Error en syncProducts:', error);
    try {
      localStorage.setItem('products_sync_error', JSON.stringify({
        message: error.message,
        timestamp: new Date().toISOString()
      }));
    } catch (e) {/* Ignorar errores de localStorage */}
    
    throw error;
  }
};

/**
 * Sincroniza órdenes/pedidos con la tienda online
 * @param {string} tiendaId - ID de la tienda (opcional, usa userData por defecto)
 * @param {Object} options - Opciones adicionales
 * @returns {Promise<Object>} Resultado de la sincronización
 */
const syncOrders = async (tiendaId = null, options = {}) => {
  try {
    // Obtener el ID de tienda desde el servicio centralizado mejorado
    // UserDataService ahora garantiza que siempre devolverá un ID válido o un ID de error
    const storeId = tiendaId || userDataService.getStoreId();
    
    // Verificar que no sea el ID de error
    if (storeId === 'ERROR_NO_STORE_ID') {
      console.error('[syncOrders] No se pudo obtener un storeId válido para la sincronización');
      throw new Error('ID de tienda no disponible o no válido');
    }
    
    // Registrar diagnósticos
    try {
      localStorage.setItem('orders_sync_start', new Date().toISOString());
    } catch (e) {/* Ignorar errores de localStorage */}
    
    // Utilizar la función syncEntity con el tipo 'orders'
    const result = await syncEntity(storeId, 'orders', {
      forceRefresh: options.forceRefresh !== false,
      timeRange: options.timeRange || 'last_month',
      ...options
    });
    
    // Registrar resultado
    try {
      localStorage.setItem('orders_sync_result', JSON.stringify({
        success: result?.success,
        count: result?.count,
        timestamp: new Date().toISOString()
      }));
    } catch (e) {/* Ignorar errores de localStorage */}
    
    return result;
  } catch (error) {
    // Registrar error
    console.error('[syncService] Error en syncOrders:', error);
    try {
      localStorage.setItem('orders_sync_error', JSON.stringify({
        message: error.message,
        timestamp: new Date().toISOString()
      }));
    } catch (e) {/* Ignorar errores de localStorage */}
    
    throw error;
  }
};

/**
 * Sincroniza clientes con la tienda online
 * @param {string} tiendaId - ID de la tienda (opcional, usa userData por defecto)
 * @param {Object} options - Opciones adicionales
 * @returns {Promise<Object>} Resultado de la sincronización
 */
const syncCustomers = async (tiendaId = null, options = {}) => {
  try {
    // Obtener el ID de tienda desde el servicio centralizado mejorado
    // UserDataService ahora garantiza que siempre devolverá un ID válido o un ID de error
    const storeId = tiendaId || userDataService.getStoreId();
    
    // Verificar que no sea el ID de error
    if (storeId === 'ERROR_NO_STORE_ID') {
      console.error('[syncCustomers] No se pudo obtener un storeId válido para la sincronización');
      throw new Error('ID de tienda no disponible o no válido');
    }
    
    // Registrar diagnósticos
    try {
      localStorage.setItem('customers_sync_start', new Date().toISOString());
    } catch (e) {/* Ignorar errores de localStorage */}
    
    // Utilizar la función syncEntity con el tipo 'customers'
    const result = await syncEntity(storeId, 'customers', {
      forceRefresh: options.forceRefresh !== false,
      ...options
    });
    
    // Registrar resultado
    try {
      localStorage.setItem('customers_sync_result', JSON.stringify({
        success: result?.success,
        count: result?.count,
        timestamp: new Date().toISOString()
      }));
    } catch (e) {/* Ignorar errores de localStorage */}
    
    return result;
  } catch (error) {
    // Registrar error
    console.error('[syncService] Error en syncCustomers:', error);
    try {
      localStorage.setItem('customers_sync_error', JSON.stringify({
        message: error.message,
        timestamp: new Date().toISOString()
      }));
    } catch (e) {/* Ignorar errores de localStorage */}
    
    throw error;
  }
};

/**
 * Exportación por defecto como objeto para facilitar simulación en pruebas
 */
export default {
  checkSyncHealth,
  processSync,
  syncEntity,
  syncRecentSales,
  syncProducts,
  syncOrders,
  syncCustomers,
  syncDashboard,
  clearCache: () => syncResultsCache.clearCache()
};
